"use client";

import type { QueryClient, QueryKey } from "@tanstack/react-query";
import {
  readOpaqueLocalStorageString,
  writeOpaqueLocalStorageString,
} from "@/lib/security/opaque-storage";
import { getConvexErrorMessage } from "@/lib/utils/convex-error-handler";

type CacheEntry<T> = {
  value: T;
  updatedAt: number;
};

type CacheRead<T> =
  | { hasValue: true; value: T; updatedAt: number }
  | { hasValue: false; value: undefined; updatedAt: undefined };

const memoryCache = new Map<string, CacheEntry<unknown>>();

const getKey = (storageKey: string, scopeKey: string) =>
  `${storageKey}::${scopeKey}`;

export const readLocalFirstCache = <T>(
  storageKey: string,
  scopeKey: string,
): CacheRead<T> => {
  const key = getKey(storageKey, scopeKey);
  const inMemory = memoryCache.get(key) as CacheEntry<T> | undefined;
  if (inMemory) {
    return { hasValue: true, value: inMemory.value, updatedAt: inMemory.updatedAt };
  }

  if (typeof window === "undefined") {
    return { hasValue: false, value: undefined, updatedAt: undefined };
  }

  try {
    const raw = readOpaqueLocalStorageString(key);
    if (!raw) {
      return { hasValue: false, value: undefined, updatedAt: undefined };
    }

    const parsed = JSON.parse(raw) as Partial<CacheEntry<T>>;
    if (!parsed || !Object.prototype.hasOwnProperty.call(parsed, "value")) {
      window.localStorage.removeItem(key);
      return { hasValue: false, value: undefined, updatedAt: undefined };
    }

    const entry: CacheEntry<T> = {
      value: parsed.value as T,
      updatedAt: typeof parsed.updatedAt === "number" ? parsed.updatedAt : 0,
    };
    memoryCache.set(key, entry);
    return { hasValue: true, value: entry.value, updatedAt: entry.updatedAt };
  } catch {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore storage failures; the live query path still works.
    }
    return { hasValue: false, value: undefined, updatedAt: undefined };
  }
};

export const hydrateLocalFirstQueryData = <T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  storageKey: string,
  scopeKey: string,
) => {
  const queryState = queryClient.getQueryState<T>(queryKey);
  if (queryState?.data !== undefined) {
    return false;
  }

  const cached = readLocalFirstCache<T>(storageKey, scopeKey);
  if (!cached.hasValue) {
    return false;
  }

  queryClient.setQueryData(queryKey, cached.value, {
    updatedAt: cached.updatedAt,
  });
  return true;
};

export const writeLocalFirstCache = <T>(
  storageKey: string,
  scopeKey: string,
  value: T,
) => {
  const key = getKey(storageKey, scopeKey);
  const entry: CacheEntry<T> = {
    value,
    updatedAt: Date.now(),
  };
  memoryCache.set(key, entry);

  if (typeof window === "undefined") {
    return;
  }

  try {
    writeOpaqueLocalStorageString(key, JSON.stringify(entry));
  } catch {
    // Local storage can be unavailable or full. The in-memory cache still helps this session.
  }
};

export const deleteLocalFirstCacheStorageKey = (storageKey: string) => {
  const prefix = `${storageKey}::`;

  Array.from(memoryCache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) {
      memoryCache.delete(key);
    }
  });

  if (typeof window === "undefined") {
    return;
  }

  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      window.localStorage.removeItem(key);
    });
  } catch {
    // Ignore storage failures; logout/auth flows should continue.
  }
};

export const localFirstQuery = <T>(
  storageKey: string,
  scopeKey: string,
  queryFn: () => Promise<T>,
) => async (): Promise<T> => {
  try {
    const value = await queryFn();
    if (value !== undefined && value !== null) {
      writeLocalFirstCache(storageKey, scopeKey, value);
    }
    return value;
  } catch (error) {
    const cached = readLocalFirstCache<T>(storageKey, scopeKey);
    if (cached.hasValue) {
      console.warn("Using cached data after Convex query failed:", getConvexErrorMessage(error));
      return cached.value;
    }

    throw new Error(getConvexErrorMessage(error));
  }
};
